// Ореол неопределённости положения снесённой точки: круг радиусом σ выбранного
// дня. Рисуется геометрией (полигон в градусах), а не пиксельным circle-radius:
// так круг остаётся привязан к местности и правильно масштабируется при зуме.
//
// Радиус растёт с днями сноса и на прогнозных течениях (ансамбль, drift_ensemble),
// и СЖИМАЕТСЯ, когда анализ заменяет прогноз и точка пере-сносится.

import type { DriftCollection, DriftProps } from '../api'

const KM_PER_DEG_LAT = 111.32
const STEPS = 48 // граней круга — гладко и дёшево

/** Полигон-круг радиусом `radiusKm` вокруг (lon,lat), замкнутое кольцо.
 *  км→градусы: по широте прямо, по долготе делим на cos(широты). */
export function circlePolygon(
  lon: number,
  lat: number,
  radiusKm: number,
  steps: number = STEPS,
): [number, number][] {
  const dLat = radiusKm / KM_PER_DEG_LAT
  const dLon = radiusKm / (KM_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180))
  const ring: [number, number][] = []
  for (let i = 0; i <= steps; i += 1) {
    const a = (i / steps) * 2 * Math.PI
    ring.push([lon + dLon * Math.cos(a), lat + dLat * Math.sin(a)])
  }
  return ring
}

/** Из нарезанной коллекции сноса — полигоны-ореолы дневных маркеров.
 *
 *  Только role='day' (яркая точка под ползунком) и только с положительным
 *  радиусом: у старых треков без ансамбля radius_km отсутствует — ореол не рисуем
 *  (честнее пусто, чем круг нулевого/неизвестного размера). */
export function haloFeatures(
  fc: DriftCollection,
): GeoJSON.Feature<GeoJSON.Polygon, DriftProps>[] {
  const out: GeoJSON.Feature<GeoJSON.Polygon, DriftProps>[] = []
  for (const f of fc.features) {
    if (f.geometry.type !== 'Point') continue
    const p = f.properties
    if (p.role !== 'day') continue
    const r = p.radius_km
    if (r == null || !(r > 0)) continue
    const [lon, lat] = f.geometry.coordinates as [number, number]
    out.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [circlePolygon(lon, lat, r)] },
      properties: p,
    })
  }
  return out
}

/** FeatureCollection ореолов (для отдельного слоя-заливки под точками). */
export function haloCollection(
  fc: DriftCollection,
): GeoJSON.FeatureCollection<GeoJSON.Polygon, DriftProps> {
  return { type: 'FeatureCollection', features: haloFeatures(fc) }
}
